import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  DesignTestInputDto,
  DesignTestOutputDto
} from '../../../models/job-request-iterations-dto.interface';

interface StaticAgingReportField {
  label: string;
  value: string;
}

interface StaticAgingReportRow {
  left: StaticAgingReportField;
  right?: StaticAgingReportField;
}

@Component({
  selector: 'app-static-aging-test-report',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './static-aging-test-report.component.html',
  styleUrl: './static-aging-test-report.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class StaticAgingTestReportComponent {
  @Input({ required: true }) test!: DesignTestInputDto;
  @Input({ required: true }) output!: DesignTestOutputDto;

  protected staticAgingReportRows(): StaticAgingReportRow[] | null {
    const fieldDefinitions: Array<{ label: string; names: string[] }> = [
      { label: 'Temperature', names: ['temperature'] },
      { label: 'Pressure', names: ['pressure'] },
      { label: 'Duration', names: ['duration'] },
      { label: 'Initial Specific Gravity', names: ['initialdensity', 'initialspecificgravity'] },
      { label: 'Mud Volume', names: ['mudvolume'] },
      { label: 'Column Mass', names: ['columnmass'] },
      { label: 'Shelf Mass', names: ['shelfmass'] },
      { label: 'Weights Mass', names: ['weightsmass'] },
      { label: 'Total Mass', names: ['totalmass'] },
      { label: 'Penetration', names: ['penetration'] },
      { label: 'Free Fluid', names: ['freefluid'] },
      { label: 'Free Fluid Volume Percent', names: ['freefluidvolumepercent'] },
      { label: 'Top Specific Gravity', names: ['topdensity', 'topspecificgravity'] },
      { label: 'Middle Specific Gravity', names: ['middledensity', 'middlespecificgravity'] },
      { label: 'Bottom Specific Gravity', names: ['bottomdensity', 'bottomspecificgravity'] },
      { label: 'ASA Combined Specific Gravity', names: ['asacombineddensity', 'asacombinedspecificgravity'] },
      { label: 'Shear Strength', names: ['shearstrength'] },
      { label: 'SAG Factor', names: ['sagfactor'] },
      { label: 'SAG Index', names: ['sagindex'] }
    ];
    const fields = fieldDefinitions
      .map(field => ({ label: field.label, value: this.value(field.names) }))
      .filter((field): field is StaticAgingReportField => field.value !== undefined);

    if (fields.length === 0) return null;

    const rows: StaticAgingReportRow[] = [];
    for (let index = 0; index < fields.length; index += 2) {
      rows.push({ left: fields[index], right: fields[index + 1] });
    }

    return rows;
  }

  protected displayValue(value: string | undefined): string {
    return value ?? '—';
  }

  private value(names: string[]): string | undefined {
    const acceptedNames = new Set(names.map(name => this.normalize(name)));
    const parsedField = [...this.output.parsedValues, ...this.test.parsedInputValues].find(field =>
      acceptedNames.has(this.normalize(field.label)) && field.value?.trim());

    return parsedField?.value?.trim()
      || this.xmlValue(this.output.content, acceptedNames)
      || this.xmlValue(this.test.content, acceptedNames)
      || undefined;
  }

  private xmlValue(content: string | null, names: Set<string>): string | null {
    if (!content) return null;

    const document = new DOMParser().parseFromString(content, 'application/xml');
    if (document.querySelector('parsererror')) return null;

    for (const element of Array.from(document.getElementsByTagName('*'))) {
      if (element.localName.toLowerCase() === 'parameter') {
        if (!names.has(this.normalize(element.getAttribute('key')))) continue;
      } else if (!names.has(this.normalize(element.localName))) {
        continue;
      }

      if (element.children.length === 0) {
        const value = element.textContent?.trim();
        if (value) return value;
      }
    }

    return null;
  }

  private normalize(value: string | null | undefined): string {
    return (value ?? '').replace(/[\s_\-/,().:%]+/g, '').toLowerCase();
  }
}